import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';

/* ═══════════════════════════════════════════════════════════
   GAMMALENS TICKER COMMAND BAR
   Single dark theme — high contrast, every element readable.
   
   Color rules:
     #f1f5f9  — primary text (tickers, headings)
     #cbd5e1  — secondary text (company names, chips)
     #94a3b8  — tertiary text (placeholders, hints)
     #64748b  — muted (keyboard hints, footers)
   
   All pass WCAG AA on #0f172a+ backgrounds.
   ═══════════════════════════════════════════════════════════ */

const SECTORS = [
  {
    id: 'etfs', label: 'ETFs', color: '#a3a3a3',
    tickers: [
      { symbol: 'SPY', name: 'S&P 500 ETF' },
      { symbol: 'QQQ', name: 'Nasdaq 100 ETF' },
      { symbol: 'IWM', name: 'Russell 2000 ETF' },
      { symbol: 'DIA', name: 'Dow Jones ETF' },
      { symbol: 'XLF', name: 'Financials ETF' },
      { symbol: 'SMH', name: 'Semiconductor ETF' },
      { symbol: 'GLD', name: 'Gold ETF' },
      { symbol: 'SLV', name: 'Silver ETF' },
      { symbol: 'XLE', name: 'Energy ETF' },
      { symbol: 'XLK', name: 'Tech ETF' },
      { symbol: 'XLV', name: 'Healthcare ETF' },
      { symbol: 'ARKK', name: 'ARK Innovation ETF' },
      { symbol: 'XLI', name: 'Industrials ETF' },
      { symbol: 'XLY', name: 'Consumer Discretionary ETF' },
      { symbol: 'XLP', name: 'Consumer Staples ETF' },
      { symbol: 'XLU', name: 'Utilities ETF' },
      { symbol: 'XLRE', name: 'Real Estate ETF' },
      { symbol: 'XLB', name: 'Materials ETF' },
      { symbol: 'XLC', name: 'Communication Services ETF' },
      { symbol: 'TLT', name: '20+ Year Treasuries ETF' },
      { symbol: 'IEF', name: '7-10Y Treasury ETF' },
      { symbol: 'HYG', name: 'High Yield Bond ETF' },
      { symbol: 'KRE', name: 'Regional Banks ETF' },
      { symbol: 'XBI', name: 'Biotech ETF' },
      { symbol: 'USO', name: 'Oil Fund ETF' },
      { symbol: 'UUP', name: 'Dollar Index ETF' },
    ],
  },
  {
    id: 'semis', label: 'Semiconductors', color: '#34c759',
    tickers: [
      { symbol: 'NVDA', name: 'NVIDIA' },
      { symbol: 'AMD', name: 'AMD' },
      { symbol: 'AVGO', name: 'Broadcom' },
      { symbol: 'SMCI', name: 'Super Micro' },
      { symbol: 'MU', name: 'Micron' },
      { symbol: 'INTC', name: 'Intel' },
      { symbol: 'TSM', name: 'TSMC' },
      { symbol: 'MRVL', name: 'Marvell' },
      { symbol: 'ARM', name: 'ARM Holdings' },
      { symbol: 'QCOM', name: 'Qualcomm' },
      { symbol: 'ASML', name: 'ASML' },
      { symbol: 'AMAT', name: 'Applied Materials' },
      { symbol: 'LRCX', name: 'Lam Research' },
      { symbol: 'KLAC', name: 'KLA' },
      { symbol: 'TXN', name: 'Texas Instruments' },
      { symbol: 'ADI', name: 'Analog Devices' },
      { symbol: 'NXPI', name: 'NXP Semiconductors' },
      { symbol: 'ON', name: 'ON Semiconductor' },
      { symbol: 'MPWR', name: 'Monolithic Power' },
    ],
  },
  {
    id: 'tech', label: 'Tech', color: '#60a5fa',
    tickers: [
      { symbol: 'AAPL', name: 'Apple' },
      { symbol: 'MSFT', name: 'Microsoft' },
      { symbol: 'GOOGL', name: 'Alphabet' },
      { symbol: 'AMZN', name: 'Amazon' },
      { symbol: 'META', name: 'Meta Platforms' },
      { symbol: 'NFLX', name: 'Netflix' },
      { symbol: 'CRM', name: 'Salesforce' },
      { symbol: 'ORCL', name: 'Oracle' },
      { symbol: 'PLTR', name: 'Palantir' },
      { symbol: 'ADBE', name: 'Adobe' },
      { symbol: 'UBER', name: 'Uber' },
      { symbol: 'SHOP', name: 'Shopify' },
      { symbol: 'SNOW', name: 'Snowflake' },
      { symbol: 'NOW', name: 'ServiceNow' },
      { symbol: 'DELL', name: 'Dell Technologies' },
      { symbol: 'BABA', name: 'Alibaba' },
      { symbol: 'RBLX', name: 'Roblox' },
      { symbol: 'IONQ', name: 'IonQ' },
      { symbol: 'DDOG', name: 'Datadog' },
      { symbol: 'MDB', name: 'MongoDB' },
      { symbol: 'TEAM', name: 'Atlassian' },
      { symbol: 'WDAY', name: 'Workday' },
      { symbol: 'S', name: 'SentinelOne' },
      { symbol: 'JD', name: 'JD.com' },
      { symbol: 'PDD', name: 'PDD Holdings' },
      { symbol: 'APP', name: 'AppLovin' },
      { symbol: 'RDDT', name: 'Reddit' },
    ],
  },
  {
    id: 'finance', label: 'Finance', color: '#fbbf24',
    tickers: [
      { symbol: 'JPM', name: 'JPMorgan Chase' },
      { symbol: 'GS', name: 'Goldman Sachs' },
      { symbol: 'BAC', name: 'Bank of America' },
      { symbol: 'MS', name: 'Morgan Stanley' },
      { symbol: 'C', name: 'Citigroup' },
      { symbol: 'WFC', name: 'Wells Fargo' },
      { symbol: 'V', name: 'Visa' },
      { symbol: 'MA', name: 'Mastercard' },
      { symbol: 'BRKB', name: 'Berkshire Hathaway B' },
      { symbol: 'AIG', name: 'AIG' },
      { symbol: 'PGR', name: 'Progressive' },
      { symbol: 'SCHW', name: 'Charles Schwab' },
      { symbol: 'IBKR', name: 'Interactive Brokers' },
    ],
  },
  {
    id: 'consumer', label: 'Consumer', color: '#fb923c',
    tickers: [
      { symbol: 'TSLA', name: 'Tesla' },
      { symbol: 'COST', name: 'Costco' },
      { symbol: 'WMT', name: 'Walmart' },
      { symbol: 'HD', name: 'Home Depot' },
      { symbol: 'MCD', name: "McDonald's" },
      { symbol: 'NKE', name: 'Nike' },
      { symbol: 'SBUX', name: 'Starbucks' },
      { symbol: 'TGT', name: 'Target' },
      { symbol: 'LOW', name: "Lowe's" },
      { symbol: 'LEN', name: 'Lennar' },
      { symbol: 'DHI', name: 'D.R. Horton' },
      { symbol: 'TOL', name: 'Toll Brothers' },
      { symbol: 'F', name: 'Ford Motor' },
      { symbol: 'GM', name: 'General Motors' },
    ],
  },
  {
    id: 'staples', label: 'Staples', color: '#d4d4d8',
    tickers: [
      { symbol: 'PG', name: 'Procter & Gamble' },
      { symbol: 'KO', name: 'Coca-Cola' },
      { symbol: 'PEP', name: 'PepsiCo' },
      { symbol: 'CL', name: 'Colgate-Palmolive' },
      { symbol: 'MO', name: 'Altria' },
      { symbol: 'KHC', name: 'Kraft Heinz' },
    ],
  },
  {
    id: 'reit', label: 'REIT', color: '#fdba74',
    tickers: [
      { symbol: 'PLD', name: 'Prologis' },
      { symbol: 'AMT', name: 'American Tower' },
      { symbol: 'EQIX', name: 'Equinix' },
    ],
  },
  {
    id: 'utilities', label: 'Utilities', color: '#84cc16',
    tickers: [
      { symbol: 'NEE', name: 'NextEra Energy' },
      { symbol: 'DUK', name: 'Duke Energy' },
      { symbol: 'SO', name: 'Southern Company' },
    ],
  },
  {
    id: 'health', label: 'Healthcare', color: '#f472b6',
    tickers: [
      { symbol: 'UNH', name: 'UnitedHealth' },
      { symbol: 'LLY', name: 'Eli Lilly' },
      { symbol: 'JNJ', name: 'Johnson & Johnson' },
      { symbol: 'PFE', name: 'Pfizer' },
      { symbol: 'ABBV', name: 'AbbVie' },
      { symbol: 'MRK', name: 'Merck' },
      { symbol: 'BMY', name: 'Bristol-Myers' },
      { symbol: 'MRNA', name: 'Moderna' },
      { symbol: 'AMGN', name: 'Amgen' },
      { symbol: 'GILD', name: 'Gilead Sciences' },
      { symbol: 'REGN', name: 'Regeneron' },
      { symbol: 'VRTX', name: 'Vertex Pharmaceuticals' },
      { symbol: 'ISRG', name: 'Intuitive Surgical' },
      { symbol: 'MDT', name: 'Medtronic' },
    ],
  },
  {
    id: 'energy', label: 'Energy', color: '#4ade80',
    tickers: [
      { symbol: 'XOM', name: 'Exxon Mobil' },
      { symbol: 'CVX', name: 'Chevron' },
      { symbol: 'OXY', name: 'Occidental' },
      { symbol: 'COP', name: 'ConocoPhillips' },
      { symbol: 'DVN', name: 'Devon Energy' },
      { symbol: 'ENPH', name: 'Enphase Energy' },
      { symbol: 'SMR', name: 'NuScale Power' },
    ],
  },
  {
    id: 'crypto', label: 'Crypto / Fintech', color: '#a78bfa',
    tickers: [
      { symbol: 'COIN', name: 'Coinbase' },
      { symbol: 'MSTR', name: 'MicroStrategy' },
      { symbol: 'IBIT', name: 'iShares Bitcoin Trust' },
      { symbol: 'FBTC', name: 'Fidelity Bitcoin ETF' },
      { symbol: 'ETHA', name: 'iShares Ethereum ETF' },
      { symbol: 'BITO', name: 'ProShares Bitcoin ETF' },
      { symbol: 'MARA', name: 'Marathon Digital' },
      { symbol: 'RIOT', name: 'Riot Platforms' },
      { symbol: 'CLSK', name: 'CleanSpark' },
      { symbol: 'SQ', name: 'Block (Square)' },
      { symbol: 'PYPL', name: 'PayPal' },
      { symbol: 'SOFI', name: 'SoFi' },
      { symbol: 'HOOD', name: 'Robinhood' },
      { symbol: 'AFRM', name: 'Affirm' },
    ],
  },
  {
    id: 'meme', label: 'Meme / High Vol', color: '#f87171',
    tickers: [
      { symbol: 'GME', name: 'GameStop' },
      { symbol: 'AMC', name: 'AMC Entertainment' },
      { symbol: 'RIVN', name: 'Rivian' },
      { symbol: 'LCID', name: 'Lucid Motors' },
      { symbol: 'NIO', name: 'NIO' },
      { symbol: 'SNAP', name: 'Snap' },
      { symbol: 'ROKU', name: 'Roku' },
      { symbol: 'DKNG', name: 'DraftKings' },
      { symbol: 'SOUN', name: 'SoundHound AI' },
      { symbol: 'CVNA', name: 'Carvana' },
      { symbol: 'UPST', name: 'Upstart' },
    ],
  },
  {
    id: 'industrial', label: 'Industrial / Defense', color: '#94a3b8',
    tickers: [
      { symbol: 'BA', name: 'Boeing' },
      { symbol: 'CAT', name: 'Caterpillar' },
      { symbol: 'LMT', name: 'Lockheed Martin' },
      { symbol: 'RTX', name: 'Raytheon' },
      { symbol: 'GE', name: 'GE Aerospace' },
      { symbol: 'DE', name: 'Deere' },
      { symbol: 'DAL', name: 'Delta Air Lines' },
      { symbol: 'UAL', name: 'United Airlines' },
      { symbol: 'CCL', name: 'Carnival' },
      { symbol: 'RKLB', name: 'Rocket Lab' },
      { symbol: 'ASTS', name: 'AST SpaceMobile' },
    ],
  },
  {
    id: 'media', label: 'Media / Telecom', color: '#e879f9',
    tickers: [
      { symbol: 'DIS', name: 'Disney' },
      { symbol: 'CMCSA', name: 'Comcast' },
      { symbol: 'T', name: 'AT&T' },
      { symbol: 'TMUS', name: 'T-Mobile' },
    ],
  },
  {
    id: 'other', label: 'Other', color: '#67e8f9',
    tickers: [
      { symbol: 'ABNB', name: 'Airbnb' },
      { symbol: 'PANW', name: 'Palo Alto Networks' },
      { symbol: 'CRWD', name: 'CrowdStrike' },
      { symbol: 'ZS', name: 'Zscaler' },
      { symbol: 'NET', name: 'Cloudflare' },
    ],
  },
];

const ALL_TICKERS = SECTORS.flatMap(s => s.tickers);
const getSector = (symbol) => SECTORS.find(s => s.tickers.find(t => t.symbol === symbol)) || SECTORS[0];
export const getTickerName = (symbol) => ALL_TICKERS.find(t => t.symbol === symbol)?.name || symbol;

/* ─── ResultRow — defined outside component to prevent remount on keystroke ─── */
function ResultRow({ t, idx, sec, highlightIdx, currentTicker, onSelect, watchlist = [], onWatchlistToggle }) {
  const isActive = currentTicker === t.symbol;
  const isHL = highlightIdx === idx;
  return (
    <div
      data-row
      onClick={() => onSelect(t.symbol)}
      style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        padding: '9px 18px',
        cursor: 'pointer',
        transition: 'background 0.08s',
        borderLeft: isActive ? `3px solid ${sec.color}` : '3px solid transparent',
        background: isHL ? 'rgba(255,255,255,0.08)' : isActive ? 'rgba(255,255,255,0.04)' : 'transparent',
      }}
      onMouseEnter={(e) => { if (!isHL) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
      onMouseLeave={(e) => { if (!isHL) e.currentTarget.style.background = isActive ? 'rgba(255,255,255,0.04)' : 'transparent'; }}
    >
      <div style={{
        width: '8px', height: '8px', borderRadius: '50%',
        background: sec.color, flexShrink: 0,
      }} />
      <span style={{
        fontFamily: "'SF Mono', 'JetBrains Mono', 'Consolas', monospace",
        fontWeight: '700', fontSize: '13px',
        color: isActive ? sec.color : '#f2f2f7',
        minWidth: '50px',
      }}>
        {t.symbol}
      </span>
      <span style={{ fontSize: '12px', color: '#a1a1aa', flex: 1 }}>
        {t.name}
      </span>
      {isActive ? (
        <span style={{
          fontSize: '9px', color: sec.color, fontWeight: '800',
          letterSpacing: '0.8px', padding: '2px 8px',
          background: sec.color + '20', borderRadius: '4px',
        }}>ACTIVE</span>
      ) : (
        <span style={{ fontSize: '9px', color: '#71717a', fontWeight: '600' }}>
          {sec.label.split(' ')[0]}
        </span>
      )}
      {onWatchlistToggle && (
        <span
          onClick={(e) => {
            e.stopPropagation();
            e.currentTarget.style.transform = 'scale(1.3)';
            setTimeout(() => { if (e.currentTarget) e.currentTarget.style.transform = 'scale(1)'; }, 150);
            onWatchlistToggle(t.symbol);
          }}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: '28px', height: '28px', padding: '8px', marginLeft: '2px',
            cursor: 'pointer', borderRadius: '50%',
            transition: 'all 0.2s cubic-bezier(0.16,1,0.3,1)',
            background: 'transparent', fontSize: '16px',
            color: watchlist.includes(t.symbol) ? '#fbbf24' : '#8e8e93',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(251,191,36,0.08)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          title={watchlist.includes(t.symbol) ? 'Remove from watchlist' : 'Add to watchlist'}
        >
          {watchlist.includes(t.symbol) ? '★' : '☆'}
        </span>
      )}
    </div>
  );
}

export default function TickerSelector({ currentTicker, onSelect, loading, watchlist = [], onWatchlistToggle, autoOpen = false }) {
  const [query, setQuery] = useState('');
  const [paletteOpen, setPaletteOpen] = useState(autoOpen);

  useEffect(() => {
    if (autoOpen) setPaletteOpen(true);
  }, [autoOpen]);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const inputRef = useRef(null);
  const paletteRef = useRef(null);
  const listRef = useRef(null);

  // "/" shortcut
  useEffect(() => {
    const handler = (e) => {
      if (e.key === '/' && !paletteOpen && !['INPUT','TEXTAREA'].includes(document.activeElement?.tagName)) {
        e.preventDefault();
        setPaletteOpen(true);
      }
      if (e.key === 'Escape' && paletteOpen) {
        setPaletteOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [paletteOpen]);

  useEffect(() => {
    if (paletteOpen) setTimeout(() => inputRef.current?.focus(), 50);
  }, [paletteOpen]);

  // Outside click handled directly on backdrop div — no extra listener needed

  const results = useMemo(() => {
    if (!query.trim()) return ALL_TICKERS;
    const q = query.toUpperCase();
    return ALL_TICKERS.filter(t =>
      t.symbol.includes(q) || t.name.toUpperCase().includes(q)
    );
  }, [query]);

  useEffect(() => { setHighlightIdx(0); }, [results]);
  useEffect(() => {
    if (listRef.current && highlightIdx >= 0) {
      const items = listRef.current.querySelectorAll('[data-row]');
      if (items[highlightIdx]) items[highlightIdx].scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIdx]);

  const selectTicker = (symbol) => {
    if (loading) return;
    onSelect(symbol);
    setPaletteOpen(false);
    setQuery('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[highlightIdx]) selectTicker(results[highlightIdx].symbol);
      else if (query.trim()) selectTicker(query.trim().toUpperCase());
    }
  };

  const flatIdx = (symbol) => ALL_TICKERS.findIndex(t => t.symbol === symbol);



  return (
    <>
      <style>{`
        @keyframes glPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes glFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes glScaleIn { from { opacity: 0; transform: scale(0.97) translateY(-6px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        @keyframes wl-glow { 0% { border-color: rgba(251,191,36,0.30); box-shadow: 0 0 12px rgba(251,191,36,0.10); } 100% { border-color: rgba(251,191,36,0.10); box-shadow: none; } }
      `}</style>

      <div>
        {/* ─── Search Bar ─── */}
        <div style={{ marginBottom: '14px' }}>
          <div
            onClick={() => setPaletteOpen(true)}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', gap: '8px',
              padding: '12px 14px',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '12px',
              cursor: 'pointer', transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
          >
            <span style={{ fontSize: '14px', color: '#a1a1aa' }}>⌕</span>
            <span style={{ fontSize: '13px', color: '#a1a1aa', flex: 1 }}>Search tickers...</span>
            <kbd style={{
              padding: '2px 8px', borderRadius: '4px',
              background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
              fontSize: '10px', color: '#a1a1aa',
              fontFamily: "'SF Mono', monospace", fontWeight: '600',
            }}>/</kbd>
          </div>
        </div>

        {/* ─── Watchlist Row ─── */}
        {watchlist.length > 0 ? (
          <div style={{
            background: 'linear-gradient(135deg, rgba(251,191,36,0.04), rgba(251,191,36,0.01))',
            border: '1px solid rgba(251,191,36,0.10)',
            borderRadius: '10px', padding: '10px 12px', marginBottom: '12px',
            display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', rowGap: '6px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0, padding: '4px 0' }}>
              <span style={{ color: '#fbbf24', fontSize: '11px', lineHeight: '12px' }}>★</span>
              <span style={{ fontSize: '9px', color: '#fbbf24', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: "'JetBrains Mono', monospace", lineHeight: '12px' }}>Watchlist</span>
            </div>
            {watchlist.map(symbol => {
              const isActive = currentTicker === symbol;
              return (
                <button key={`wl-${symbol}`} onClick={() => selectTicker(symbol)} disabled={loading} style={{
                  padding: '4px 12px', borderRadius: '6px', border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                  background: isActive ? 'rgba(251,191,36,0.15)' : 'rgba(251,191,36,0.06)',
                  color: isActive ? '#fbbf24' : '#e2e8f0',
                  fontSize: '12px', fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.02em',
                  borderWidth: '1px', borderStyle: 'solid',
                  borderColor: isActive ? '#fbbf2450' : 'rgba(251,191,36,0.12)',
                  boxShadow: isActive ? '0 2px 12px rgba(251,191,36,0.10)' : 'none',
                  transition: 'all 0.2s cubic-bezier(0.16,1,0.3,1)',
                  opacity: loading ? 0.4 : 1,
                }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(251,191,36,0.08)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = isActive ? '0 2px 12px rgba(251,191,36,0.10)' : 'none'; }}
                >{symbol}</button>
              );
            })}
            <span style={{ fontSize: '9px', color: watchlist.length >= 10 ? '#ff453a' : '#636366', fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, marginLeft: 'auto' }}>
              {watchlist.length}/10
            </span>
          </div>
        ) : onWatchlistToggle ? (
          <div style={{
            background: 'linear-gradient(135deg, rgba(251,191,36,0.03), rgba(251,191,36,0.01))',
            border: '1px dashed rgba(251,191,36,0.15)',
            borderRadius: '10px', padding: '12px 16px', marginBottom: '14px',
            display: 'flex', alignItems: 'center', gap: '10px',
          }}>
            <span style={{ color: '#fbbf24', fontSize: '16px' }}>☆</span>
            <div>
              <div style={{ fontSize: '10px', color: '#fbbf24', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: "'JetBrains Mono', monospace", marginBottom: '2px' }}>Watchlist</div>
              <div style={{ fontSize: '11px', color: '#636366', fontFamily: "'JetBrains Mono', monospace" }}>Click ☆ on any ticker to pin it here for quick access</div>
            </div>
          </div>
        ) : null}
      </div>

      {/* ─── Command Palette ─── */}
      {paletteOpen && ReactDOM.createPortal((
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
            paddingTop: '10vh', animation: 'glFadeIn 0.12s ease',
            background: 'rgba(0,0,0,0.75)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
          onMouseDown={(e) => {
            // Only close if clicking the backdrop itself, not children
            if (e.target === e.currentTarget) { setPaletteOpen(false); setQuery(''); }
          }}
        >
          <div ref={paletteRef} style={{
            position: 'relative', width: '100%', maxWidth: '540px', margin: '0 20px',
            background: '#111114',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: '18px',
            boxShadow: '0 32px 100px rgba(0,0,0,0.85), 0 0 0 1px rgba(255,255,255,0.04)',
            overflow: 'hidden',
            animation: 'glScaleIn 0.18s cubic-bezier(0.16, 1, 0.3, 1)',
          }}>
            {/* Search */}
            <div style={{
              padding: '16px 18px',
              borderBottom: '1px solid rgba(255,255,255,0.08)',
              display: 'flex', alignItems: 'center', gap: '10px',
              background: 'rgba(255,255,255,0.04)',
            }}>
              <span style={{ fontSize: '16px', color: '#a1a1aa' }}>⌕</span>
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search by ticker or company name..."
                style={{
                  flex: 1, background: 'transparent', border: 'none',
                  color: '#f2f2f7', fontSize: '16px', outline: 'none',
                  fontFamily: "'SF Mono', 'JetBrains Mono', 'Consolas', monospace",
                }}
              />
              <kbd style={{
                padding: '3px 8px', borderRadius: '5px',
                background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
                fontSize: '10px', color: '#a1a1aa',
                fontFamily: "'SF Mono', monospace", fontWeight: '600',
              }}>ESC</kbd>
            </div>

            {/* Results */}
            <div ref={listRef} style={{ maxHeight: '380px', overflowY: 'auto' }}>
              {!query.trim() ? (
                SECTORS.map(sec => (
                  <div key={sec.id}>
                    <div style={{
                      padding: '12px 18px 4px',
                      fontSize: '10px', fontWeight: '800',
                      color: sec.color, letterSpacing: '1.5px',
                    }}>
                      {sec.label.toUpperCase()}
                    </div>
                    {sec.tickers.map(t => (
                      <ResultRow key={t.symbol} t={t} idx={flatIdx(t.symbol)} sec={sec} highlightIdx={highlightIdx} currentTicker={currentTicker} onSelect={selectTicker} watchlist={watchlist} onWatchlistToggle={onWatchlistToggle} />
                    ))}
                  </div>
                ))
              ) : (
                results.length > 0 ? results.map((t, idx) => (
                  <ResultRow key={t.symbol} t={t} idx={idx} sec={getSector(t.symbol)} highlightIdx={highlightIdx} currentTicker={currentTicker} onSelect={selectTicker} watchlist={watchlist} onWatchlistToggle={onWatchlistToggle} />
                )) : (
                  <div style={{ padding: '32px', textAlign: 'center' }}>
                    <div style={{ fontSize: '13px', color: '#d4d4d8', marginBottom: '6px' }}>
                      No results for "{query}"
                    </div>
                    <div style={{ fontSize: '11px', color: '#a1a1aa' }}>
                      Press Enter to analyze any custom ticker
                    </div>
                  </div>
                )
              )}
            </div>

            {/* Footer */}
            <div style={{
              padding: '10px 18px',
              borderTop: '1px solid rgba(255,255,255,0.08)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: 'rgba(255,255,255,0.04)',
            }}>
              <div style={{ display: 'flex', gap: '14px' }}>
                {[
                  { key: '↑↓', label: 'navigate' },
                  { key: '↵', label: 'select' },
                  { key: 'esc', label: 'close' },
                ].map(s => (
                  <span key={s.key} style={{ fontSize: '10px', color: '#71717a' }}>
                    <kbd style={{
                      padding: '1px 5px', borderRadius: '3px',
                      background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                      fontFamily: 'monospace', fontSize: '9px', marginRight: '3px',
                      color: '#a1a1aa',
                    }}>{s.key}</kbd>
                    {s.label}
                  </span>
                ))}
              </div>
              <span style={{ fontSize: '10px', color: '#71717a' }}>
                {results.length} tickers
              </span>
            </div>
          </div>
        </div>
      ), document.body)}
    </>
  );
}
